<template>
	<div>
		<!-- Content section -->
		<div v-if="contentOnly" class="connection-info-container">
			<div
				class="connection-info-item"
				v-for="item in connectionInfoItems"
				:key="item.id || item.label"
			>
				<div class="info-list-label">{{ item.label }}:</div>
				<div class="info-list-value">
					{{ item.value }}
				</div>
			</div>
		</div>

		<!-- Sidebar section -->
		<div v-if="sidebarOnly">
			<SidebarSection>
				<InfoList :items="settingsItems" :title="$t('common_settingsapp')">
					<template #label="{ item }">
						<div v-if="item.type === 'toggle'" class="label-tip">
							<label>{{ item.label }}</label>
							<Tippy
								v-if="item.tooltip"
								arrow
								theme="large"
								:content="item.tooltip"
								:options="{ animation: 'scale' }"
							>
								<CircleQuestionIcon regular class="help-icon" />
							</Tippy>
						</div>
					</template>

					<!-- Custom value slot -->
					<template #value="{ item }">
						<ToggleSwitch
							v-if="item.type === 'toggle'"
							v-model="item.value"
							:disabled="isSavingConfiguration"
							@change="handleSettingChange(item.key, $event)"
							class="settings-toggle-switch"
						/>
						<Button
							v-else-if="item.type === 'link'"
							link
							:href="item.url"
							:to="item.route"
							:target="item.target"
							class="settings-link full-width"
						>
							{{ item.linkText }}
							<ArrowUpRightFromSquareIcon type="solid" />
						</Button>
						<span v-else>{{ item.value || '-' }}</span>
					</template>
				</InfoList>
			</SidebarSection>

			<SidebarSection v-if="showRoleMapping">
				<InfoList
					:items="actionsInfoItems"
					:title="t('admin_groups_actions_label')"
				>
					<!-- Same slot/classes as the Settings labels so colors match -->
					<template #label="{ item }">
						<div class="label-tip">
							<label>{{ item.label }}</label>
						</div>
					</template>

					<template #value="{ item }">
						<Button
							v-if="item.key === 'role_mapping'"
							link
							class="role-mapping-button"
							@click="emit('editRoleMappings')"
						>
							<PlusIcon v-if="!hasRoleMappings" type="regular" />
							{{
								hasRoleMappings
									? t('admin_integrations_edit')
									: t('common_add_button_title')
							}}
						</Button>
					</template>
				</InfoList>
			</SidebarSection>

			<SidebarSection>
				<ActionList
					localize
					:items="actionItems"
					:title="showRoleMapping ? null : 'admin_groups_actions_label'"
					@select="onSelectAction"
				/>
			</SidebarSection>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Button, useAlert, Tippy, Table, Input } from '@/components/common'
import {
	TrashCanIcon,
	CircleQuestionIcon,
	ArrowUpRightFromSquareIcon,
	PlusIcon,
} from '@/icons'
import ToggleSwitch from '@/components/common/ToggleSwitch'
import { InfoList, ActionList, SidebarSection } from '../../common'

// Props
const props = defineProps({
	// eslint-disable-next-line vue/no-unused-properties -- reserved for the max group size row (currently commented out)
	connection: {
		type: Object,
		required: false,
		default: null,
	},
	configuration: {
		type: Object,
		required: false,
		default: null,
	},
	// eslint-disable-next-line vue/no-unused-properties -- part of the shared connection-details component contract
	platformConfiguration: {
		type: Object,
		required: false,
		default: null,
	},
	contentOnly: {
		type: Boolean,
		default: false,
	},
	sidebarOnly: {
		type: Boolean,
		default: false,
	},
	// Disables the toggles while any configure PATCH is in flight, so an
	// overlapping change can't send stale flag values
	isSavingConfiguration: {
		type: Boolean,
		default: false,
	},
	// eslint-disable-next-line vue/no-unused-properties -- part of the shared connection-details component contract
	isSyncingAll: {
		type: Boolean,
		default: false,
	},
})

// Utilities
const router = useRouter()
const { t } = useI18n()
const alert = useAlert()

const emit = defineEmits(['delete', 'settingChange', 'editRoleMappings'])

const showRoleMapping = computed(
	() => !!props.configuration && 'role_mappings' in props.configuration
)

const hasRoleMappings = computed(() =>
	Object.values(props.configuration?.role_mappings || {}).some(
		(names) => Array.isArray(names) && names.length
	)
)

const actionsInfoItems = computed(() => [
	{
		key: 'role_mapping',
		label: t('admin_integrations_role_mapping'),
	},
])

// Reactive State
const actionItems = [
	{
		id: 'delete',
		icon: TrashCanIcon,
		type: 'danger',
		label: 'admin_integrations_delete_connection',
	},
]

const settingsItems = computed(() => [
	{
		key: 'sync_enabled',
		label: t('admin_integrations_sync_enabled'),
		tooltip: t('admin_integrations_google_sync_enabled_desc'),
		type: 'toggle',
		value: props.configuration?.sync_enabled ?? false,
	},
	{
		key: 'configuration_instructions',
		type: 'link',
		linkText: t('admin_integrations_configuration_instructions'),
		url: 'https://docs.google.com/workspace-integration',
		target: '_blank',
	},
])

// Computed
const connectionInfoItems = computed(() => [
	// {
	// 	label: 'MAX GROUP SIZE',
	// 	value: props.connection?.max_group_size || 'Unlimited'
	// },
])

// Methods
const handleSettingChange = async (key, newValue) => {
	emit('settingChange', {
		[key]: newValue,
	})
}

const onSelectAction = (action) => {
	switch (action.id) {
		case 'delete':
			emit('delete')
			break
	}
}

onMounted(async () => {})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.connection-info-container {
	padding-top: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: flex-start;

	.connection-info-item {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		gap: 6px;
		font-family: Avenir-Roman;
		font-size: 14px;
		color: var(--neutral-800);

		.info-list-label {
			font-size: 11px;
			color: var(--neutral-600);
		}
	}
}

.table-wrapper {
	padding-top: 11px;
	.table-header {
		font-size: 24px;
		font-family: Avenir-Heavy;
		color: var(--neutral-800);
		margin-bottom: 20px;
		padding-left: 24px;
	}
	.resources-table {
		margin-top: 15px;
	}
	.search-bar-container {
		padding-right: 24px;
		padding-left: 24px;
	}
}

// Settings styling
// Deliberately not a flex row: these labels wrap at the sidebar's 240px, and a
// flex icon would centre itself against the whole wrapped block instead of
// trailing the last word. Inline flow keeps it next to the text either way.
.label-tip {
	label {
		// _common.scss blockifies every bare <label>, which would drop the icon
		// onto its own line now that this isn't a flex row
		display: inline;
		color: var(--neutral-600);
		font-family: Avenir-Roman;
		font-size: 14px;
	}

	.help-icon {
		color: var(--neutral-600);
		font-size: 12px;
		cursor: help;
		margin-left: 6px;
		vertical-align: middle;
	}
}

.settings-toggle-switch {
	font-size: 24px;
}

// Override InfoList spacing for toggles
:deep(.info-list-item) {
	align-items: center;
	min-height: 32px;
}

.role-mapping-button {
	padding: 0;
	gap: 6px;
	height: auto;

	.pronto-icon {
		font-size: 12px;
	}
}

.settings-link {
	padding: 0;
	&.full-width {
		width: 100%;
	}
	.pronto-icon {
		font-size: 12px;
	}
}

// Override InfoList item styling for link items
:deep(.info-list-item:has(.full-width)) {
	.info-list-label {
		display: none;
	}
	.info-list-value {
		flex: 1;
	}
}
</style>
